import { Clock, FileText } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { CharterExportButton } from '../Charter/CharterExportButton'
import type { CharterVersion } from '../../redux/types/charterTypes'

interface CharterVersionHistoryProps {
  versions: CharterVersion[]
  roomId: string
  teamId: number
  currentVersion: number
}

export function CharterVersionHistory({
  versions,
  roomId,
  teamId,
  currentVersion,
}: CharterVersionHistoryProps) {
  if (!versions.length) {
    return (
      <div className='flex items-center justify-center h-32 text-gray-400 text-sm'>
        No version history yet.
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
        <Clock className='h-5 w-5 text-gray-400' />
        Version History
      </h2>

      <div className='relative'>
        {/* Timeline line */}
        <div className='absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200' />

        <div className='space-y-4 ml-10'>
          {[...versions].reverse().map((version, idx) => {
            const isCurrent = version.version_number === currentVersion
            const date = new Date(version.ratified_at)

            return (
              <div key={version.id} className='relative'>
                {/* Dot */}
                <div
                  className={`absolute -left-[2.375rem] top-3 w-3.5 h-3.5 rounded-full border-2 ${
                    isCurrent
                      ? 'border-indigo-600 bg-indigo-600'
                      : 'border-gray-300 bg-white'
                  }`}
                />

                <Card className={`${isCurrent ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200'}`}>
                  <CardHeader className='pb-2'>
                    <div className='flex items-center justify-between'>
                      <CardTitle className='text-sm font-semibold text-gray-900 flex items-center gap-2'>
                        <FileText className='h-4 w-4 text-gray-400' />
                        Version {version.version_number}
                        {isCurrent && (
                          <Badge className='text-xs bg-indigo-100 text-indigo-700 border-indigo-200'>
                            Current
                          </Badge>
                        )}
                      </CardTitle>
                      <CharterExportButton
                        roomId={roomId}
                        teamId={teamId}
                        version={version.version_number}
                        size='sm'
                        variant='ghost'
                      />
                    </div>
                    <p className='text-xs text-gray-500'>
                      Ratified {date.toLocaleDateString()} at {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </CardHeader>
                  {(version.change_summary || idx < versions.length - 1) && (
                    <CardContent className='pt-0'>
                      <Separator className='mb-3' />
                      {version.change_summary ? (
                        <p className='text-xs text-gray-600'>{version.change_summary}</p>
                      ) : idx === versions.length - 1 ? (
                        <p className='text-xs text-gray-400 italic'>Initial charter ratification.</p>
                      ) : null}
                    </CardContent>
                  )}
                </Card>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
